import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Image as ImageIcon, Upload, RotateCcw, ExternalLink, AlertTriangle, Loader2,
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { useToast } from '../components/ui/use-toast';
import { confirm } from '../components/ui/confirm-dialog';
import AccessDenied from '../components/AccessDenied';
import { useRestaurant } from '../context/RestaurantContext';
import { useModuleAccess } from '../utils/useModuleAccess';
import { locationsService } from '../services/locationsService';
import { sitePhotosService } from '../services/sitePhotosService';

/** Website Photos: replace a branch's public pictures without a redeploy, always restorable to the supplied photo. */

// Slot titles — mirrors tis-website/assets/README.txt.
const SLOT_HELP = {
  storefront: { title: 'Hero background' },
  room: { title: 'The Room' },
  score: { title: 'The Score' },
  visit: { title: 'Visit' },
  logo: { title: 'Logo' },
};

const MAX_MB = 5;

function SlotCard({ slot, canEdit, busy, onUpload, onRestore }) {
  const fileRef = useRef(null);
  const help = SLOT_HELP[slot.slot] || { title: slot.slot };

  const choose = (e) => {
    const file = e.target.files?.[0];
    // Let the same file be picked again after a failed upload.
    e.target.value = '';
    if (file) onUpload(slot.slot, file);
  };

  return (
    <Card className="overflow-hidden">
      <div className="relative aspect-[16/9] bg-neutral-100 dark:bg-neutral-800">
        {slot.image ? (
          <img src={slot.image} alt={help.title} className="h-full w-full object-cover" />
        ) : (
          <div className="flex h-full w-full flex-col items-center justify-center gap-2 text-neutral-400">
            <ImageIcon className="h-8 w-8" />
            <span className="text-xs">Showing the supplied photo</span>
          </div>
        )}
        {busy && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/40">
            <Loader2 className="h-6 w-6 animate-spin text-white" />
          </div>
        )}
      </div>

      <CardContent className="space-y-3 p-4">
        <div>
          <div className="flex items-center gap-2">
            <h3 className="font-semibold">{help.title}</h3>
            {slot.is_custom && (
              <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-medium text-emerald-700
                               dark:bg-emerald-900/40 dark:text-emerald-300">
                Your photo
              </span>
            )}
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          <input
            ref={fileRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            className="hidden"
            onChange={choose}
          />
          <Button
            type="button"
            size="sm"
            disabled={!canEdit || busy}
            onClick={() => fileRef.current?.click()}
          >
            <Upload className="mr-2 h-4 w-4" />
            {slot.is_custom ? 'Replace' : 'Upload'}
          </Button>
          {slot.is_custom && (
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={!canEdit || busy}
              onClick={() => onRestore(slot.slot, help.title)}
            >
              <RotateCcw className="mr-2 h-4 w-4" />
              Restore supplied photo
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

// `embedded` renders this as a tab inside Website Settings, which already provides the page chrome.
export default function WebsitePhotos({ embedded = false }) {
  const { restaurantId } = useRestaurant();
  const { canView, canEdit } = useModuleAccess('settings');
  const { toast } = useToast();

  const [locations, setLocations] = useState([]);
  const [locationId, setLocationId] = useState(null);
  const [slots, setSlots] = useState([]);
  const [slug, setSlug] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busySlot, setBusySlot] = useState(null);

  // Branches first — the photos belong to one branch, not the restaurant.
  useEffect(() => {
    if (!restaurantId) return;
    let cancelled = false;
    locationsService
      .list(restaurantId)
      .then((locs) => {
        if (cancelled) return;
        setLocations(locs);
        setLocationId((id) => id ?? (locs[0]?.id ?? null));
        if (!locs.length) setLoading(false);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e?.response?.data?.error || e.message || 'Failed to load branches');
        setLoading(false);
      });
    return () => { cancelled = true; };
  }, [restaurantId]);

  const load = useCallback(async () => {
    if (!restaurantId || !locationId) return;
    setLoading(true);
    try {
      const { slots: rows, publicSiteSlug } = await sitePhotosService.list(restaurantId, locationId);
      setSlots(rows);
      setSlug(publicSiteSlug);
      setError(null);
    } catch (e) {
      setError(e?.response?.data?.error || e.message || 'Failed to load photos');
    } finally {
      setLoading(false);
    }
  }, [restaurantId, locationId]);

  useEffect(() => { load(); }, [load]);

  const handleUpload = async (slot, file) => {
    if (file.size > MAX_MB * 1024 * 1024) {
      toast({
        title: 'That image is too large',
        description: `Keep photos under ${MAX_MB} MB so the website stays quick to load.`,
        variant: 'destructive',
      });
      return;
    }
    setBusySlot(slot);
    try {
      setSlots(await sitePhotosService.upload(restaurantId, locationId, slot, file));
      toast({ title: 'Photo updated', description: 'Your website is showing it now.' });
    } catch (e) {
      toast({
        title: 'Upload failed',
        description: e?.response?.data?.error || e.message,
        variant: 'destructive',
      });
    } finally {
      setBusySlot(null);
    }
  };

  const handleRestore = async (slot, title) => {
    const ok = await confirm({
      title: `Restore the supplied ${title.toLowerCase()} photo?`,
      description: 'Your uploaded photo is deleted and the website goes back to the picture it came with.',
      confirmText: 'Restore',
      tone: 'destructive',
    });
    if (!ok) return;
    setBusySlot(slot);
    try {
      setSlots(await sitePhotosService.remove(restaurantId, locationId, slot));
      toast({ title: 'Supplied photo restored' });
    } catch (e) {
      toast({
        title: 'Could not restore',
        description: e?.response?.data?.error || e.message,
        variant: 'destructive',
      });
    } finally {
      setBusySlot(null);
    }
  };

  if (!canView) return <AccessDenied perm="settings.view" what="website photos" />;

  const publicUrl = slug ? `${window.location.origin}/site/?outlet=${slug}` : '';

  return (
    <div className={embedded ? 'space-y-6' : 'space-y-6 p-6'}>
      {!embedded && (
        <div>
          <h1 className="font-display text-3xl font-semibold text-foreground tracking-tight leading-none">Website Photos</h1>
        </div>
      )}

      {locations.length > 1 && (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm text-muted-foreground">Branch:</span>
          {locations.map((loc) => (
            <Button
              key={loc.id}
              type="button"
              size="sm"
              variant={loc.id === locationId ? 'default' : 'outline'}
              onClick={() => setLocationId(loc.id)}
            >
              {loc.name}
            </Button>
          ))}
        </div>
      )}

      {/* Uploads are per branch, and without a website address the branch has no public page to show them on. */}
      {!loading && !slug && locationId && (
        <div className="flex items-start gap-3 rounded-lg border border-amber-300 bg-amber-50 p-4
                        text-sm text-amber-900 dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-200">
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0" />
          <p className="font-medium">
            This branch has no public website address yet — uploaded photos are saved
            but won't show until you set one in Settings → Locations.
          </p>
        </div>
      )}

      {publicUrl && (
        <a
          href={publicUrl}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-2 text-sm text-primary hover:underline"
        >
          <ExternalLink className="h-4 w-4" />
          View this branch's website
        </a>
      )}

      {error && (
        <div className="rounded-lg border border-red-300 bg-red-50 p-4 text-sm text-red-800
                        dark:border-red-800 dark:bg-red-950/40 dark:text-red-200">
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex items-center gap-2 text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading photos…
        </div>
      ) : !locations.length ? (
        <p className="text-muted-foreground">Add a branch in Settings → Locations first.</p>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {slots.map((slot) => (
            <SlotCard
              key={slot.slot}
              slot={slot}
              canEdit={canEdit}
              busy={busySlot === slot.slot}
              onUpload={handleUpload}
              onRestore={handleRestore}
            />
          ))}
        </div>
      )}
    </div>
  );
}
